import type { NodeV2 } from '@vedalib/editor'
import * as R from 'ramda'
import React, { useCallback } from 'react'

import type { IGridArea } from 'components/Editor/NodeV2/formControls/Area/types'
import { safeParseArea } from 'components/Editor/NodeV2/formControls/Area/utils'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import { dragEnd, dragMove, dragStart, updateBlock } from 'services/Store/Project/actions'
import { useProjectDispatch } from 'services/Store/Project/hooks'
import { setGridArea } from 'services/Store/Project/mutators/node'
import { mergeNodeStyle } from 'services/Store/Project/selectors'
import type { Block, IBlockMode, MapType } from 'services/Store/Project/types'
import store from 'services/Store/store'

import { ResizeControlActionUiEnum } from '../../enums'
import type { ICoordinates } from '../../types'
import { CALCULATE_AREA, getGroupArea, isOffset, remapNodesRelativeToArea } from '../../utils'
import * as s from './CellResizeControl.module.scss'

const TYPE_MAP = {
  [ResizeControlActionUiEnum.TOP_LEFT]: {
    placement: 'topLeft',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nwse-resize',
  },
  [ResizeControlActionUiEnum.TOP]: {
    placement: 'top',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ns-resize',
  },
  [ResizeControlActionUiEnum.TOP_RIGHT]: {
    placement: 'topRight',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nesw-resize',
  },
  [ResizeControlActionUiEnum.RIGHT]: {
    placement: 'right',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ew-resize',
  },
  [ResizeControlActionUiEnum.BOTTOM_RIGHT]: {
    placement: 'bottomRight',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nwse-resize',
  },
  [ResizeControlActionUiEnum.BOTTOM]: {
    placement: 'bottom',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ns-resize',
  },
  [ResizeControlActionUiEnum.BOTTOM_LEFT]: {
    placement: 'bottomLeft',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'nesw-resize',
  },
  [ResizeControlActionUiEnum.LEFT]: {
    placement: 'left',
    translateX: '-50%',
    translateY: '-50%',
    cursor: 'ew-resize',
  },
} as const

interface IResizeControlProps {
  type: ResizeControlActionUiEnum
  selected: NodeV2[]
  node: NodeV2 | null
  mode: IBlockMode
  block: Block
}

const ResizeControl: React.FC<IResizeControlProps> = ({ type, selected, mode, block, node }) => {
  const dispatch = useProjectDispatch()
  const { deviceMode } = mode
  const { cursor, ...portalProps } = TYPE_MAP[type]
  const area = getGroupArea(selected, deviceMode)

  const onMouseDown = useCallback(
    (downEvent: React.MouseEvent) => {
      downEvent.stopPropagation()
      if (downEvent.button !== 0) {
        return
      }

      const parentStyles = mergeNodeStyle(node?.style || {}, deviceMode)

      let raf: number | null = null
      let isDragging = false
      let placement = R.clone(area)
      let initialCoord: ICoordinates | null = null
      const targetIds = selected.map((node) => node.id)
      const areas = selected
        .map((node) => mergeNodeStyle(node.style, deviceMode))
        .map((style) => safeParseArea(style.gridArea))

      initialCoord = { x: downEvent.clientX, y: downEvent.clientY }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (raf) {
          return
        }
        raf = requestAnimationFrame(() => {
          raf = null
          if (!isDragging && initialCoord && isOffset(initialCoord, moveEvent) && node) {
            dispatch(
              dragStart({
                initialCoords: initialCoord,
                sourceId: node.id,
                box: null,
                targetIds,
                type,
              }),
            )
            isDragging = true
            document.getSelection()?.removeAllRanges()
          }

          if (isDragging && parentStyles && initialCoord && node) {
            const currentCoords = { x: moveEvent.clientX, y: moveEvent.clientY }

            placement = CALCULATE_AREA[type](currentCoords, placement, node.id)
            const placeholder = remapNodesRelativeToArea(areas, area, placement)

            dispatch(
              dragMove({
                zone: 'canvas',
                destinationId: node.id,
                currentCoords,
                areas: selected.reduce((acc, node, idx) => {
                  acc[node.id] = placeholder[idx]
                  return acc
                }, {} as MapType<IGridArea>),
                flex: null,
                index: null,
                coords: null,
                guides: null,
              }),
            )
          }
        })
      }

      const handleMouseUp = (upEvent: MouseEvent) => {
        upEvent.stopPropagation()
        document.removeEventListener('mouseup', handleMouseUp, true)
        document.removeEventListener('mousemove', handleMouseMove, true)
        const state = store.getState().project
        const dnd = state.state.editor.dnd
        const newBlock = R.compose(
          ...(selected.map((node) => setGridArea(node.id, deviceMode, dnd?.areas?.[node.id])) as [
            (block: Block) => Block,
          ]),
        )(block)
        dispatch(updateBlock({ id: block?.uuid, value: newBlock }))
        dispatch(dragEnd())
        initialCoord = null
        isDragging = false
      }

      document.addEventListener('mouseup', handleMouseUp, true)
      document.addEventListener('mousemove', handleMouseMove, true)
    },
    [selected, area, deviceMode, node, dispatch, type, block],
  )

  return (
    <AbsolutePortal {...portalProps} chain='off' name={type}>
      <div className={s.root} onMouseDown={onMouseDown} style={{ cursor }}>
        <div className={s.control} />
      </div>
    </AbsolutePortal>
  )
}

export default ResizeControl
